/**
 * Superadmin tenant DETAIL PAGE (ADR-0036 Phase 2) — routed at `/platform/tenants/:id`,
 * replacing the old crowded side drawer. A clean header (name + status + identity + live
 * counts) over TABS that split the concerns: Panoramica (health), Abbonamento (plan +
 * usage), Profilo (billing/anagrafica + logo).
 *
 * There is deliberately NO "Accessi" tab: per-user impersonation was RETIRED (ADR-0081
 * D6/S0, not repaired). Entering a customer's instance will come back as a partner
 * session minting an EPHEMERAL admin principal — ADR-0081 S3 — not as a borrowed user.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ArrowLeft,
  Building2,
  CreditCard,
  Loader2,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { formatPrice } from "@/lib/api-billing";
import { BillingOverviewView } from "@/components/billing/BillingOverviewView";
import { SubscriptionCarePanel } from "@/components/billing/SubscriptionCarePanel";
import { TenantProfileForm } from "@/components/settings/TenantProfileForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OrganizationLinkDialog } from "@/pages/platform/OrganizationLinkDialog";
import { PageContainer, PageContent } from "@/components/ui/page";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type Subscription,
  type TenantDetail,
  type TenantHealth,
  type TenantStatus,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";
import { Input } from "@/components/ui/input";
import type { TenantProfileIn } from "@/lib/tenantProfile";

function statusTone(status: TenantStatus): "active" | "warning" | "neutral" {
  if (status === "active") return "active";
  if (status === "suspended") return "warning";
  return "neutral";
}

function statusLabel(status: TenantStatus): string {
  return { active: "Attivo", suspended: "Sospeso", provisioning: "In creazione" }[status] ?? status;
}

export function PlatformTenantDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const detailQ = useQuery({
    queryKey: ["platform", "tenants", id],
    queryFn: () => platformApi.getTenant(id),
    enabled: !!id,
  });
  const t = detailQ.data;

  const back = (
    <button
      type="button"
      onClick={() => navigate("..")}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
      Tenant
    </button>
  );

  if (detailQ.isLoading) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
          </div>
        </div>
      </PageContainer>
    );
  }
  if (detailQ.isError || !t) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-start gap-2 text-sm text-danger">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            {detailQ.error instanceof PlatformApiError ? detailQ.error.message : "Errore di rete."}
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Tabs
        defaultValue="overview"
        orientation="vertical"
        className="flex h-full min-h-0 flex-col"
      >
        {/* Header band — back-link, identity, live metrics. Full-width. */}
        <div className="shrink-0 border-b border-border/60 px-4 pt-5 pb-4 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-3 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-surface text-muted-foreground">
                <Building2 className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
                    {t.name || t.slug}
                  </h1>
                  <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge>
                </div>
                <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                  {t.slug} · {t.schema_name} · creato{" "}
                  {new Date(t.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <Metric label="Tabelle" value={t.table_count} />
              <span className="h-9 w-px bg-border/60" />
              <Metric label="Utenti" value={t.user_count} />
            </div>
          </div>
        </div>

        {/* Body — vertical tab rail + full-width content (control-panel layout). */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="shrink-0 border-b border-border/60 p-3 md:w-56 md:border-b-0 md:border-r">
            <TabsList className="gap-0.5 bg-transparent">
              <VTab value="overview" icon={Activity}>Panoramica</VTab>
              <VTab value="billing" icon={CreditCard}>Abbonamento</VTab>
              <VTab value="profile" icon={Building2}>Profilo</VTab>
            </TabsList>
          </div>

          <PageContent className="pt-6">
            <TabsContent value="overview" className="grid gap-4 lg:grid-cols-2">
              <TenantHealthSection tenantId={t.id} />
              <TenantIdentityCard tenant={t} />
            </TabsContent>

            <TabsContent value="billing" className="space-y-5">
              <TenantSubscriptionSection tenant={t} />
              <TenantUsageSection tenantId={t.id} />
            </TabsContent>

            <TabsContent value="profile">
              <PlatformTenantProfileSection tenantId={t.id} />
            </TabsContent>
          </PageContent>
        </div>
      </Tabs>
    </PageContainer>
  );
}

/** Health drill-down (ADR-0036 T1+T2) — migration drift + schema completeness +
 * footprint, with a one-click "Migra" when the tenant is behind head. */
function TenantHealthSection({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const key = ["platform", "tenant-health", tenantId];
  const q = useQuery({ queryKey: key, queryFn: () => platformApi.tenantHealth(tenantId) });
  const migrate = useMutation({
    mutationFn: () => platformApi.migrateTenant(tenantId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["platform", "tenants", "overview"] });
    },
  });
  const h: TenantHealth | undefined = q.data;

  return (
    <Card
      title="Salute"
      icon={Activity}
      action={
        h ? (
          h.hosted_here === false ? (
            <Badge tone="neutral">Su cella</Badge>
          ) : (
            <Badge tone={h.ok ? "active" : "warning"}>{h.ok ? "OK" : "Attenzione"}</Badge>
          )
        ) : undefined
      }
    >
      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
        </div>
      ) : h && h.hosted_here === false ? (
        // Split Control: the tenant's schema lives on its CELL's DB — probing it
        // here would fabricate "every table missing"; migrations run on the cell
        // (its deploy migrate Job), so there is no "Migra ora" either.
        <p className="text-sm text-muted-foreground">
          Ospitato sulla sua cella: schema e migrazioni vivono lì (il Job di migrazione della
          cella li aggiorna a ogni deploy). La diagnostica locale non si applica da Control.
        </p>
      ) : h && h.migration && h.schema ? (
        <div className="space-y-3 text-sm">
          <Field label="Migrazione">
            {h.migration.behind ? (
              <span className="text-warning">
                indietro ({h.migration.current ?? "—"} → {h.migration.head ?? "—"})
              </span>
            ) : (
              <span className="text-success">a head ({h.migration.current ?? "—"})</span>
            )}
          </Field>
          <Field label="Schema">
            {h.schema.complete ? (
              <span className="text-success">{h.schema.table_count} tabelle · completo</span>
            ) : (
              <span className="text-warning" title={h.schema.missing_tables.join(", ")}>
                {h.schema.missing_tables.length} tabelle mancanti
              </span>
            )}
          </Field>
          {h.footprint && (
            <Field label="Footprint">
              <span className="text-muted-foreground">
                {h.footprint.active_users ?? 0}/{h.footprint.users ?? 0} utenti · {h.footprint.apps ?? 0} app ·{" "}
                {h.footprint.workflows ?? 0} workflow
              </span>
            </Field>
          )}
          {h.migration.behind && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={migrate.isPending}
              onClick={() => migrate.mutate()}
            >
              {migrate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Wrench className="h-4 w-4" strokeWidth={1.5} />
              )}
              Migra ora
            </Button>
          )}
          {migrate.data && !migrate.data.ok && (
            <p className="text-xs text-danger">{migrate.data.error}</p>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** Billing/anagrafica + logo for ONE tenant (ADR-0036 Fase 2) — the superadmin twin of
 * the operator's own Settings panel; both share `TenantProfileForm`. */
function PlatformTenantProfileSection({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const key = ["platform", "tenant-profile", tenantId];
  const q = useQuery({ queryKey: key, queryFn: () => platformApi.getTenantProfile(tenantId) });
  const onSettled = () => void qc.invalidateQueries({ queryKey: key });
  const save = useMutation({
    mutationFn: (b: TenantProfileIn) => platformApi.updateTenantProfile(tenantId, b),
    onSettled,
  });
  const upload = useMutation({
    mutationFn: (f: File) => platformApi.uploadTenantLogo(tenantId, f),
    onSettled,
  });
  const removeLogo = useMutation({
    mutationFn: () => platformApi.deleteTenantLogo(tenantId),
    onSettled,
  });

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <h3 className="text-sm font-medium">Profilo &amp; fatturazione</h3>
      </div>
      <TenantProfileForm
        value={q.data}
        loading={q.isPending}
        saving={save.isPending}
        uploadingLogo={upload.isPending}
        onSave={(b) => save.mutate(b)}
        onUploadLogo={(f) => upload.mutate(f)}
        onDeleteLogo={() => removeLogo.mutate()}
      />
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  );
}

/** A flat header metric (label over value) — no box, control-panel style. */
function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

/** A vertical-rail tab trigger (left accent border + filled when active). */
function VTab({
  value,
  icon: Icon,
  children,
}: {
  value: string;
  icon: typeof Activity;
  children: React.ReactNode;
}) {
  return (
    <TabsTrigger value={value}>
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      {children}
    </TabsTrigger>
  );
}

/** A titled surface card with an optional icon + right-aligned action — the
 * shared container for the detail tabs' sections (matches the list pages' chrome). */
function Card({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon?: typeof Activity;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border/60 bg-surface/40">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Identity / anagrafica quick facts for the Panoramica grid — fills the column
 * next to Salute so the overview uses the full page width instead of one lonely card. */
function TenantIdentityCard({ tenant }: { tenant: TenantDetail }) {
  // ADR-0076 fetta 2: the link to the commercial record is EDITABLE from here. Moving a
  // workspace between Organizzazioni is a pure re-attribution (one UPDATE) — the deployment,
  // its plan and its data are untouched — which is what makes "duplicates are cured
  // afterwards" (decision 9) an affordable policy.
  const [linking, setLinking] = React.useState(false);
  return (
    <Card title="Anagrafica" icon={Building2}>
      {linking && (
        <OrganizationLinkDialog
          target={{ kind: "tenant", id: tenant.id, label: tenant.name || tenant.slug }}
          currentOrganizationId={tenant.organization_id}
          onClose={() => setLinking(false)}
        />
      )}
      <dl className="space-y-3 text-sm">
        <Field label="Organizzazione">
          <span className="flex items-center gap-2">
            {tenant.organization_id ? (
              <Link
                to={`/platform/organizations?focus=${tenant.organization_id}`}
                className="underline-offset-2 hover:underline"
              >
                {tenant.organization_name || "—"}
              </Link>
            ) : (
              <span className="text-muted-foreground">Non collegato</span>
            )}
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setLinking(true)}>
              {tenant.organization_id ? "Cambia" : "Collega"}
            </Button>
          </span>
        </Field>
        <Field label="Stato">
          <Badge tone={statusTone(tenant.status)}>{statusLabel(tenant.status)}</Badge>
        </Field>
        <Field label="Slug">
          <span className="font-mono text-xs">{tenant.slug}</span>
        </Field>
        <Field label="Schema">
          <span className="font-mono text-xs">{tenant.schema_name}</span>
        </Field>
        <Field label="Creato">{new Date(tenant.created_at).toLocaleDateString()}</Field>
        <Field label="ID">
          <span className="font-mono text-xs text-muted-foreground">{tenant.id}</span>
        </Field>
      </dl>
    </Card>
  );
}

/** The tenant's consumption (current plan + usage meters), read-only — the SAME overview
 *  the tenant sees in its own Abbonamento section (ADR-0041 §11). */
function TenantUsageSection({ tenantId }: { tenantId: string }) {
  const q = useQuery({
    queryKey: ["platform", "tenants", tenantId, "usage"],
    queryFn: () => platformApi.tenantUsage(tenantId),
  });
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">Consumi</h3>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      ) : q.data ? (
        <BillingOverviewView data={q.data} variant="compact" />
      ) : (
        <p className="text-sm text-muted-foreground">Consumi non disponibili.</p>
      )}
    </section>
  );
}

/** Billing — the tenant's subscription (ADR-0041 §13). Assign a plan (snapshots it),
 * re-snapshot to push a plan edit, or cancel (→ the UNLIMITED no-op). */
function TenantSubscriptionSection({ tenant }: { tenant: TenantDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const subKey = ["platform", "tenants", tenant.id, "subscription"];
  const subQ = useQuery({ queryKey: subKey, queryFn: () => platformApi.getSubscription(tenant.id) });
  const plansQ = useQuery({
    queryKey: ["platform", "plans"],
    queryFn: () => platformApi.listPlans(),
  });
  const [planId, setPlanId] = React.useState("");
  const [billingPeriod, setBillingPeriod] = React.useState<"month" | "year">("month");
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: subKey });
    void qc.invalidateQueries({ queryKey: ["platform", "tenants", tenant.id, "usage"] });
    void qc.invalidateQueries({ queryKey: ["platform", "tenants", tenant.id, "payments"] });
    void qc.invalidateQueries({ queryKey: ["platform", "tenants", tenant.id, "timeline"] });
  };

  const assign = useMutation({
    mutationFn: () => platformApi.assignSubscription(tenant.id, planId, billingPeriod),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (resume: boolean) => platformApi.cancelSubscription(tenant.id, !resume),
    onSuccess: invalidate,
  });
  const resnap = useMutation({
    mutationFn: () => platformApi.resnapshotSubscription(tenant.id),
    onSuccess: invalidate,
  });

  const sub = subQ.data;
  const plans = (plansQ.data ?? []).filter((p) => p.active);
  const currentPlan = (plansQ.data ?? []).find((p) => p.key === sub?.plan_key);

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">Gestione abbonamento</h3>
      {subQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      ) : sub ? (
        <div className="space-y-3 rounded-lg border border-border/60 p-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <Badge tone="active">{currentPlan?.name ?? sub.plan_key ?? "—"}</Badge>
            {currentPlan && (
              <span className="font-medium tabular-nums text-foreground">
                {formatPrice(currentPlan, t, sub.billing_period === "year" ? "year" : "month")}
              </span>
            )}
            <Badge tone="neutral">{sub.billing_period === "year" ? "Annuale" : "Mensile"}</Badge>
            <span className="text-muted-foreground">{sub.status}</span>
          </div>
          <SubscriptionCarePanel sub={sub} onChanged={invalidate} />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={resnap.isPending}
              title="Ri-applica lo snapshot dal piano corrente"
              onClick={() => resnap.mutate()}
            >
              Re-snapshot
            </Button>
            {sub.cancel_at_period_end ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(true)}
              >
                Riprendi abbonamento
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={cancel.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      "Annullare l'abbonamento? Resterà attivo fino a fine periodo, poi il tenant verrà sospeso. (NON diventa illimitato.)",
                    )
                  )
                    cancel.mutate(false);
                }}
              >
                <span className="text-danger">Annulla</span>
              </Button>
            )}
          </div>
          <ConcurrencyOverrideEditor tenant={tenant} sub={sub} onChanged={invalidate} />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <select
            aria-label="Piano"
            className="h-9 flex-1 rounded-md border border-border bg-transparent px-2 text-sm"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
          >
            <option value="">Nessun piano — seleziona…</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Cadenza"
            className="h-9 rounded-md border border-border bg-transparent px-2 text-sm"
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value as "month" | "year")}
          >
            <option value="month">Mensile</option>
            <option value="year">Annuale</option>
          </select>
          <Button size="sm" disabled={!planId || assign.isPending} onClick={() => assign.mutate()}>
            Assegna
          </Button>
        </div>
      )}
    </section>
  );
}

/** Per-tenant OPERATIONAL concurrency override (ADR-0040) — bump THIS tenant's caps, separate
 * from the license plan. Empty = inherit (plan → global default). Requires an active sub. */
function ConcurrencyOverrideEditor({
  tenant,
  sub,
  onChanged,
}: {
  tenant: TenantDetail;
  sub: Subscription;
  onChanged: () => void;
}) {
  const limits = (sub.overrides?.limits ?? {}) as Record<string, number | undefined>;
  const [wf, setWf] = React.useState(
    limits.max_concurrent_workflow_runs != null ? String(limits.max_concurrent_workflow_runs) : "",
  );
  const [etl, setEtl] = React.useState(
    limits.max_concurrent_etl_runs != null ? String(limits.max_concurrent_etl_runs) : "",
  );
  const settingsQ = useQuery({
    queryKey: ["platform", "settings"],
    queryFn: () => platformApi.getSettings(),
  });
  const g = settingsQ.data;

  const save = useMutation({
    mutationFn: () =>
      platformApi.setConcurrencyOverrides(tenant.id, {
        max_concurrent_workflow_runs: wf.trim() === "" ? null : Math.max(0, Number(wf) || 0),
        max_concurrent_etl_runs: etl.trim() === "" ? null : Math.max(0, Number(etl) || 0),
      }),
    onSuccess: onChanged,
  });

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border/60 p-3">
      <p className="text-xs font-medium text-foreground">Override concorrenza (operativo)</p>
      <p className="text-[11px] text-muted-foreground">
        Sovrascrive i cap di concorrenza SOLO per questo tenant (manopola infra, non di licenza).
        Vuoto = eredita il default globale.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-xs">
          <span className="block text-muted-foreground">Workflow</span>
          <Input
            type="number"
            min={0}
            aria-label="Override workflow"
            className="h-8 w-28"
            value={wf}
            onChange={(e) => setWf(e.target.value)}
            placeholder={g ? `= globale: ${g.max_concurrent_workflow_runs}` : "globale"}
          />
        </label>
        <label className="space-y-1 text-xs">
          <span className="block text-muted-foreground">ETL</span>
          <Input
            type="number"
            min={0}
            aria-label="Override ETL"
            className="h-8 w-28"
            value={etl}
            onChange={(e) => setEtl(e.target.value)}
            placeholder={g ? `= globale: ${g.max_concurrent_etl_runs}` : "globale"}
          />
        </label>
        <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate()}>
          Salva override
        </Button>
      </div>
    </div>
  );
}
